import { Form, Card, Alert, Button, Spinner, Badge } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

interface UserDifyConfigTabProps {
  userId: number
  orgDifyEnabled: boolean
}

export const UserDifyConfigTab = ({ userId, orgDifyEnabled }: UserDifyConfigTabProps) => {
  const [usePersonal, setUsePersonal] = useState(false)
  const [difyApiKey, setDifyApiKey] = useState('')
  const [difyApiUrl, setDifyApiUrl] = useState('https://api.dify.ai/v1')
  const [difyAppId, setDifyAppId] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showApiKey, setShowApiKey] = useState(false)

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`/api/users/${userId}/dify-config/`)
        setUsePersonal(response.data.use_personal || false)
        setDifyApiKey(response.data.dify_api_key || '')
        setDifyApiUrl(response.data.dify_api_url || 'https://api.dify.ai/v1')
        setDifyAppId(response.data.dify_app_id || '')
      } catch (error) {
        console.warn('No Dify config found for user, using defaults')
      } finally {
        setLoading(false)
      }
    }

    loadConfig()
  }, [userId])

  const handleSave = async () => {
    if (usePersonal && !difyApiKey) {
      toast.warning('Please enter your API Key')
      return
    }

    setSaving(true)
    try {
      await axios.post(`/api/users/${userId}/dify-config/`, {
        use_personal: usePersonal,
        dify_api_key: difyApiKey,
        dify_api_url: difyApiUrl,
        dify_app_id: difyAppId,
      })
      toast.success('Dify configuration saved')
    } catch (error: any) {
      toast.error('Error saving configuration: ' + (error.response?.data?.error || error.message))
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (!difyApiKey || !difyApiUrl) {
      toast.warning('Please enter API Key and URL')
      return
    }

    try {
      await axios.get(`${difyApiUrl}/parameters`, {
        headers: { Authorization: `Bearer ${difyApiKey}` },
        timeout: 5000,
      })
      toast.success('✅ Dify connection verified')
    } catch (error: any) {
      const errorMsg = error.response?.status === 401
        ? 'Invalid API Key'
        : error.message || 'Connection error'
      toast.error(`❌ Error: ${errorMsg}`)
    }
  }

  if (loading) {
    return (
      <div className="pt-4">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div className="pt-4">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <span className="me-2">🤖</span>
            Personal Dify Settings
          </h5>
        </Card.Header>
        <Card.Body>
          {orgDifyEnabled ? (
            <>
              <Alert variant="info" className="mb-4">
                <small>
                  Your organization has Dify enabled. You will use that configuration by default.
                </small>
              </Alert>

              <Form.Group className="mb-4">
                <Form.Check
                  type="switch"
                  label="Use my own Dify credentials"
                  checked={usePersonal}
                  onChange={(e) => setUsePersonal(e.target.checked)}
                  id="dify-personal-switch"
                />
                <Form.Text className="text-muted d-block mt-2">
                  If enabled, your messages will route through your own Dify account, allowing you to use custom models and settings.
                </Form.Text>
              </Form.Group>

              {usePersonal && (
                <>
                  {/* API Key */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      API Key
                      <span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <div className="input-group">
                      <Form.Control
                        type={showApiKey ? 'text' : 'password'}
                        value={difyApiKey}
                        onChange={(e) => setDifyApiKey(e.target.value)}
                        placeholder="app-xxxxx"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowApiKey(!showApiKey)}
                        title={showApiKey ? 'Hide' : 'Show'}
                      >
                        {showApiKey ? '👁️‍🗨️' : '🔒'}
                      </Button>
                    </div>
                    <Form.Text className="text-muted d-block mt-2">
                      Get your API Key from your{' '}
                      <a href="https://cloud.dify.ai" target="_blank" rel="noopener noreferrer">
                        Dify dashboard
                      </a>
                    </Form.Text>
                  </Form.Group>

                  {/* API URL */}
                  <Form.Group className="mb-3">
                    <Form.Label>API URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={difyApiUrl}
                      onChange={(e) => setDifyApiUrl(e.target.value)}
                      placeholder="https://api.dify.ai/v1"
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      Default: https://api.dify.ai/v1
                    </Form.Text>
                  </Form.Group>

                  {/* App ID */}
                  <Form.Group className="mb-3">
                    <Form.Label>App ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={difyAppId}
                      onChange={(e) => setDifyAppId(e.target.value)}
                      placeholder="Application UUID in Dify"
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      Required to route messages to the correct App.
                    </Form.Text>
                  </Form.Group>

                  {/* Test Connection Button */}
                  <div className="mb-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleTestConnection}
                      disabled={!difyApiKey}
                      className="me-2"
                    >
                      💾 Test Connection
                    </Button>
                  </div>

                  {/* Warning Box */}
                  <div className="bg-warning bg-opacity-10 p-3 rounded border-start border-warning border-4 mb-3">
                    <small className="text-dark d-block">
                      <strong>⚠️ Important:</strong> Your API Key will be stored securely.
                    </small>
                  </div>
                </>
              )}

              {/* Save Button */}
              {usePersonal && (
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      '💾 Save changes'
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setUsePersonal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {!usePersonal && (
                <div className="bg-light p-3 rounded border-start border-info border-4">
                  <small className="text-muted d-block">
                    <strong>💡 Note:</strong> You are currently using your organization's configuration.
                  </small>
                </div>
              )}
            </>
          ) : (
            <Alert variant="warning">
              <small>
                ⚠️ Dify is not enabled for your organization. Contact your administrator to enable it.
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
